/**
 * Category Mapping Configuration for Localhost Development
 * 
 * This file provides category slug mappings for local development.
 * In production/dev/staging, the default mappings in utils/getters.ts are used.
 * 
 * To use this configuration for localhost:
 * 1. Create .env.local file in trans.info directory
 * 2. Add: NEXT_PUBLIC_USE_LOCALHOST_CATEGORIES=true
 * 3. Adjust the category slugs below to match your local WordPress categories
 * 
 * IMPORTANT: This should ONLY be enabled on localhost to avoid breaking dev/staging!
 */

export interface CategoryMapping {
  transport: string;
  logistics: string;
  startups: string;
  trucking: string;
  shipment: string; // Spedycja/Forwarders
  interviews: string;
  premium: string;
  video: string;
  technology: string;
  magazine: string;
  analitics: string;
}

/**
 * Default production mappings (from utils/getters.ts)
 */
export const productionCategories: CategoryMapping = {
  transport: 'transport',
  logistics: 'logistics',
  startups: 'startups',
  trucking: 'trucking',
  shipment: 'forwarders',
  interviews: 'interviews',
  premium: 'premium',
  video: 'videos',
  technology: 'technology',
  magazine: 'magazine',
  analitics: 'analitics',
};

/**
 * Localhost development mappings
 * 
 * These map to the actual category slugs in your local WordPress/Elasticsearch
 * Frontend routes (from locales/pl/main.json):
 * - /pl/transport (transportLink: "transport")
 * - /pl/logistyka (logisticsLink: "logistyka")
 * - /pl/kierowcy (truckingLink: "kierowcy")
 * - /pl/spedycja (shipmentLink: "spedycja")
 * 
 * Backend categories available:
 * - biznes (Business) - ID 14937
 * - logistyka (Logistics) - ID 14935
 * - transport-drogowy (Road Transport) - ID 14934
 * - prawo-i-regulacje (Law and Regulations) - ID 14936
 */
export const localhostCategories: CategoryMapping = {
  transport: 'transport-drogowy',      // /pl/transport → transport-drogowy
  logistics: 'logistyka',              // /pl/logistyka → logistyka
  startups: 'biznes',                  // Business (closest match)
  trucking: 'transport-drogowy',       // /pl/kierowcy → transport-drogowy (drivers)
  shipment: 'biznes',                  // /pl/spedycja → biznes (forwarders/business)
  interviews: 'uncategorized',         // No interviews category yet
  premium: 'premium',                  // Premium
  video: 'videos',                     // Videos
  technology: 'uncategorized',         // No technology category yet
  magazine: 'uncategorized',           // No magazine category yet
  analitics: 'uncategorized',          // No analytics category yet
};

/**
 * Get category mapping based on environment
 */
export const getCategoryMapping = (): CategoryMapping => {
  // Only use localhost categories if explicitly enabled via env var
  // This prevents breaking dev/staging environments
  if (process.env.NEXT_PUBLIC_USE_LOCALHOST_CATEGORIES === 'true') {
    return localhostCategories;
  }

  // Use production categories for all other environments (dev, staging, production)
  return productionCategories;
};

/**
 * Get REST API category name for a given category key
 */
export const getRestCategoryName = (categoryKey: keyof CategoryMapping): string => {
  const mapping = getCategoryMapping();
  return mapping[categoryKey] || mapping.transport; // fallback to transport
};